import React from 'react';
import { STATUS_MAP, SUBSTATUS_LABELS } from '../../utils/catalogs';
import { getChartColor } from '../../utils/chartColors';

/**
 * The status of a shipment, in the same colour as the chip that filters by it.
 *
 * The colour comes from `chartColors` and not from the catalog. The catalog's
 * palette is made of light tones — on a dark surface they land as light text on
 * a dark tint, which is right, and on white they landed as light text on white:
 * 1.4:1 against a 4.5:1 minimum, a label nobody could read. And it gives eight
 * statuses the same grey, so the badge disagreed with the chip beside it about
 * what colour a status is.
 *
 * So the colour now lives in the tint, the border and the dot, and the label
 * takes the theme's own text — which is what the dark side was already doing by
 * accident, and the reason it looked fine there.
 *
 * A status outside both palettes keeps the neutral grey and shows its system
 * name rather than being hidden: an unknown status is news, not something to
 * swallow.
 *
 * @param {object} props
 * @param {string} props.status - System name, as the backend sends it.
 * @param {string} [props.label] - Overrides the catalog's name. For a caller
 *   that already resolved it across more than one catalog: an order waiting to
 *   be picked up is `in_preparation`, which lives in the order statuses and not
 *   in `STATUS_MAP`, so the badge would otherwise print the system name.
 * @param {string} [props.substatus] - Appended after a dash when it has a label.
 *   `not_specified` maps to an empty string in the catalog, so it drops out.
 * @param {'pill'|'inline'|'strong'} [props.variant] - `pill` for a table cell,
 *   where the status is a column of its own and a row of loud badges would
 *   drown the data next to it. `inline` is one more fact in a line of text: it
 *   keeps the coloured dot but inherits the colour of the text around it,
 *   because half the catalog is pale grey and would vanish as a label.
 *   `strong` is for a badge that is the headline of its own screen — bigger, a
 *   deeper tint and a full-strength border. It stays a tint rather than a solid
 *   fill because no single text colour survives this palette: white fails on
 *   fifteen of the nineteen statuses and dark text fails on the other five.
 * @param {string} [props.testId]
 */
export const StatusBadge = ({
  status,
  label: labelOverride,
  substatus,
  variant = 'pill',
  testId,
}) => {
  const label = labelOverride || STATUS_MAP[status]?.user_name || status;
  const color = getChartColor(status);
  const substatusLabel = substatus ? SUBSTATUS_LABELS[substatus] : '';

  if (variant === 'inline') {
    return (
      <span data-testid={testId} className="inline-flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
        {label}
        {substatusLabel && <span className="text-text-muted">· {substatusLabel}</span>}
      </span>
    );
  }

  const isStrong = variant === 'strong';

  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center rounded-full border text-ninja-dark dark:text-white ${
        isStrong
          ? 'gap-2 px-3.5 py-1.5 text-sm font-bold'
          : 'gap-1.5 px-2.5 py-1 text-xs font-semibold'
      }`}
      style={{
        backgroundColor: `${color}${isStrong ? '40' : '1a'}`,
        borderColor: isStrong ? color : `${color}80`,
      }}
    >
      <span
        data-testid={testId ? `${testId}-dot` : undefined}
        className={`rounded-full shrink-0 ${isStrong ? 'w-2 h-2' : 'w-1.5 h-1.5'}`}
        style={{ backgroundColor: color }}
      />
      {label}
      {substatusLabel && <span className="font-medium text-text-secondary">· {substatusLabel}</span>}
    </div>
  );
};
